import {Component, CUSTOM_ELEMENTS_SCHEMA, Inject, LOCALE_ID, NO_ERRORS_SCHEMA, OnDestroy, OnInit} from '@angular/core';
import {DispatchScheduleService} from "@/app/services/dispatch-schedule.service";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {CookieService} from "ngx-cookie-service";
import {Subject, takeUntil} from "rxjs";
import {EventNode} from "@/app/model/interface/event-node";
import {flattenDisplayLabel} from "@/app/services/format.service";
import {ScheduleTableComponent} from "@/app/standAlone/schedule/schedule-table/schedule-table.component";
import {Product} from "@/app/model";
import {KeyPerformanceMetric} from "@/app/model/interface/event";
import {DateTime} from "luxon";
import {NgIf} from "@angular/common";
import {PanelModule} from "primeng/panel";
import {Event} from "@/app/model/interface/event";

@Component({
    selector: 'app-dispatch',
    imports: [
        ScheduleTableComponent,
        TranslatePipe,
        NgIf,
        PanelModule
    ],
    standalone: true,
    templateUrl: './dispatch-schedule-view.component.html',
    styleUrl: './dispatch-schedule-view.component.scss',
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA
    ]
})
export class DispatchScheduleViewComponent implements OnInit, OnDestroy {

    // @ViewChild('todayTable', {static: false}) todayTable: Table;

    fetchingData = true;
    private ngUnsubscribe: Subject<any> = new Subject();
    scheduleStatuses: any[] = [];

    constructor(
        private dispatchScheduleService: DispatchScheduleService,
        private cookieService: CookieService,
        private translate: TranslateService,
        @Inject(LOCALE_ID) public locale: string
    ) {

        const {eventNodes$} = this.dispatchScheduleService;
        eventNodes$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
            (response) => {
                this.allEventNodes = response;
                this.groupNodes();
            }
        );

    }

    todayNodes: EventNode[] = [];
    futureNodes: EventNode[] = [];
    allEventNodes: EventNode[] = [];

    ngOnInit(): void {
        this.dispatchScheduleService.getEvents();
        this.locale = this.locale || 'en-us';
    }

    ngOnDestroy() {
        this.dispatchScheduleService.clear();
        this.ngUnsubscribe.next(null);
    }

    setTranslations() {
        this.scheduleStatuses = [
            {label: this.translate.instant("DISPATCH_SCHEDULE.SCHEDULE_STATUS.COMPLETED"), value: 'COMPLETED'},
            {label: this.translate.instant('DISPATCH_SCHEDULE.SCHEDULE_STATUS.ONGOING'), value: 'ONGOING'},
            {label: this.translate.instant('DISPATCH_SCHEDULE.SCHEDULE_STATUS.COMPLETED'), value: 'OVERLAPPING'},
            {label: this.translate.instant('DISPATCH_SCHEDULE.SCHEDULE_STATUS.UPCOMING'), value: 'UPCOMING'},
        ];
    }

    groupNodes() {
        this.setTranslations()
        this.futureNodes = [];
        this.todayNodes = [];

        this.allEventNodes.forEach((node) => {
            node.site_name = flattenDisplayLabel(this.locale, node.site_display_label);
            const productTimezone = node.event.product?.timezone;
            const regTimezone = node.registration?.timezone;
            node.timezone = productTimezone || regTimezone || 'UTC';
            node.performance_value = this.getPerformance(node)

            const relativeNow = DateTime.now().setZone(node.timezone);
            const relativeToday = DateTime.now().startOf('day').setZone(node.timezone);
            const relativeTomorrow = DateTime.now().plus({days: 1}).startOf('day').setZone(node.timezone);
            const localNodeStart = DateTime.fromISO(node.event_node_start_dttm_utc).setZone(node.timezone);

            if (node.event.event_action_type === 'STANDBY' && localNodeStart < relativeToday) {
                return;
            }

            if (localNodeStart > relativeToday) {
                //UPCOMING
                if (localNodeStart > relativeTomorrow) {
                    if (node.event.event_action_type === 'STANDBY') {
                        node.scheduleStatus = 'STANDBY'
                    } else {
                        node.scheduleStatus = 'UPCOMING'
                    }
                    this.futureNodes.push(node)
                } else {
                    //STANDBY
                    if (node.event.event_action_type === 'STANDBY') {
                        node.scheduleStatus = 'STANDBY'
                    }
                    //ASSET ACTIVE IN ANOTHER DISPATCH
                    else if (node.workflow_status == 'IN_OVERLAPPING_EVENT') {
                        node.scheduleStatus = 'OVERLAPPING'
                    }
                    //COMPLETED
                    else if (node.workflow_status == 'RESTORE_COMPLETE' || node.workflow_status == 'WORKFLOW_COMPLETE') {
                        node.scheduleStatus = 'COMPLETED'
                    }
                    //UPCOMING
                    else if (localNodeStart > relativeNow) {
                        node.scheduleStatus = 'UPCOMING'
                    }
                    //ONGOING
                    else {
                        node.scheduleStatus = 'ONGOING'
                    }

                    this.todayNodes.push(node)
                }
            }
        });
        this.fetchingData = false

    }

    getPerformance(node: EventNode) {
        const product = node.event.product;
        if (product?.target_type === 'RANGE' && node.registration_type === 'LOAD_DROP_TO') {
            return null;
        }
        if (node.scheduleStatus == 'COMPLETED') {
            return this.getOverallNodePerformancePercentage(node)
        } else {
            return (node.last_current_performance_percentage || 0)
        }
    }

    getOverallNodePerformancePercentage(node: EventNode) {
        const expectedCapacity = node.expected_capacity_value;
        const performanceVal = this.getOverallNodePerformance(node, node.event.product);
        return expectedCapacity === 0 ? 0 : Math.round(performanceVal || 0 / expectedCapacity * 100);
    }

    getOverallNodePerformance(node: EventNode, product: Product | undefined) {
        let perfValue;
        const perfMetric = product ? product.key_performance_metric : KeyPerformanceMetric.AVERAGE;
        switch (perfMetric) {
            case KeyPerformanceMetric.AVERAGE:
                perfValue = node.average_performance_value;
                break;
            case  KeyPerformanceMetric.MAXIMUM:
                perfValue = node.maximum_performance_value;
                break;
            case KeyPerformanceMetric.MINIMUM:
                perfValue = node.minimum_performance_value;
                break;
        }
        return perfValue;
    }
}
